import { useState, useRef, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Upload, FileSpreadsheet, ArrowRight, Check, AlertCircle, Loader2, X, FileText, ArrowRightLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function AccountingImportModal({ open, onClose, importType, onImportComplete, defaults = {} }) {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mappings, setMappings] = useState({});
  const [importResult, setImportResult] = useState(null);

  const reset = () => {
    setStep(1);
    setLoading(false);
    setPreview(null);
    setMappings({});
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['.xlsx', '.xls', '.csv', '.pdf'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowed.includes(ext)) {
      toast.error('Please upload an Excel (.xlsx, .xls), CSV, or PDF file');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(
        `${API}/api/accounting/import/preview?import_type=${importType}`,
        formData,
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' } }
      );
      setPreview(res.data);
      setMappings(res.data.auto_mappings || {});
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to read file');
    } finally {
      setLoading(false);
    }
  };

  const handleMapping = (systemField, fileColumn) => {
    setMappings(prev => {
      const next = { ...prev };
      if (fileColumn === '__none__') {
        delete next[systemField];
      } else {
        next[systemField] = fileColumn;
      }
      return next;
    });
  };

  const mappedPreview = useMemo(() => {
    if (!preview) return [];
    return preview.preview_data.slice(0, 10).map(row => {
      const mapped = {};
      for (const field of preview.system_fields) {
        const col = mappings[field.field];
        mapped[field.field] = col ? (row[col] || '') : '';
      }
      return mapped;
    });
  }, [preview, mappings]);

  const requiredFieldsMapped = useMemo(() => {
    if (!preview) return false;
    return preview.system_fields
      .filter(f => f.required)
      .every(f => mappings[f.field]);
  }, [preview, mappings]);

  const handleImport = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/accounting/import/execute`, {
        session_id: preview.session_id,
        mappings,
        import_type: importType,
        defaults
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      setImportResult(res.data);
      setStep(4);
      toast.success(res.data.message);
      if (onImportComplete) onImportComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const typeLabel = importType === 'bill' ? 'Bills' : importType === 'bank_transaction' ? 'Bank Transactions' : 'Invoices';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Import {typeLabel} — Step {step} of 4
          </DialogTitle>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex items-center gap-2 mb-4">
          {[
            { n: 1, label: 'Upload' },
            { n: 2, label: 'Map' },
            { n: 3, label: 'Preview' },
            { n: 4, label: 'Done' }
          ].map(({ n, label }) => (
            <div key={n} className="flex items-center gap-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold
                ${step >= n ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500'}`}>
                {step > n ? <Check className="w-3.5 h-3.5" /> : n}
              </div>
              <span className={`text-xs ${step >= n ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>{label}</span>
              {n < 4 && <ArrowRight className="w-3 h-3 text-slate-300 mx-1" />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-slate-400 cursor-pointer transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="import-dropzone"
            >
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv,.pdf" className="hidden" onChange={handleFileUpload} />
              {loading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  <p className="text-sm text-slate-500">Reading file...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="flex gap-2">
                    <FileSpreadsheet className="w-8 h-8 text-emerald-500" />
                    <FileText className="w-8 h-8 text-red-500" />
                  </div>
                  <p className="text-sm font-medium text-slate-700">Click to upload Excel, CSV, or PDF</p>
                  <p className="text-xs text-slate-400">Supported: .xlsx, .xls, .csv, .pdf</p>
                </div>
              )}
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-xs text-slate-500 space-y-1">
              <p className="font-medium text-slate-700">Tips:</p>
              <p>- First row should contain column headers</p>
              <p>- Dates can be in YYYY-MM-DD, MM/DD/YYYY, or DD/MM/YYYY format</p>
              <p>- Amounts can include $ and comma separators</p>
              <p>- For PDFs, tabular data will be extracted automatically</p>
            </div>
          </div>
        )}

        {/* Step 2: Column Mapping */}
        {step === 2 && preview && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{preview.filename}</p>
                <p className="text-xs text-slate-500">{preview.total_rows} rows found, {preview.headers.length} columns detected</p>
              </div>
              <Badge variant="outline" className="text-xs">
                <ArrowRightLeft className="w-3 h-3 mr-1" /> Map Columns
              </Badge>
            </div>

            <div className="border rounded-lg divide-y max-h-[50vh] overflow-y-auto">
              {preview.system_fields.map(field => (
                <div key={field.field} className="flex items-center gap-4 p-3 hover:bg-slate-50" data-testid={`mapping-${field.field}`}>
                  <div className="w-1/3">
                    <span className="text-sm font-medium">{field.label}</span>
                    {field.required && <span className="text-red-500 ml-1 text-xs">*</span>}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="w-1/2">
                    <Select
                      value={mappings[field.field] || '__none__'}
                      onValueChange={(v) => handleMapping(field.field, v)}
                    >
                      <SelectTrigger className="h-9" data-testid={`mapping-select-${field.field}`}>
                        <SelectValue placeholder="Select column..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- Skip --</SelectItem>
                        {preview.headers.map(h => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-8">
                    {mappings[field.field] && <Check className="w-4 h-4 text-emerald-500" />}
                  </div>
                </div>
              ))}
            </div>

            {/* Sample data preview */}
            {preview.preview_data.length > 0 && (
              <details className="text-xs">
                <summary className="text-slate-500 cursor-pointer hover:text-slate-700">Show raw data sample ({Math.min(5, preview.preview_data.length)} rows)</summary>
                <div className="overflow-x-auto mt-2 border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {preview.headers.map(h => <TableHead key={h} className="text-xs whitespace-nowrap py-1 px-2">{h}</TableHead>)}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.preview_data.slice(0, 5).map((row, i) => (
                        <TableRow key={i}>
                          {preview.headers.map(h => <TableCell key={h} className="text-xs py-1 px-2 whitespace-nowrap">{row[h]}</TableCell>)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </details>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => { setStep(1); setPreview(null); }}>Back</Button>
              <Button onClick={() => setStep(3)} disabled={!requiredFieldsMapped} data-testid="import-next-preview">
                Preview Mapped Data <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Preview Mapped Data */}
        {step === 3 && preview && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Mapped Data Preview ({Math.min(10, mappedPreview.length)} of {preview.total_rows} rows)</p>
              {!requiredFieldsMapped && (
                <Badge variant="destructive" className="text-xs"><AlertCircle className="w-3 h-3 mr-1" /> Missing required mappings</Badge>
              )}
            </div>

            <div className="overflow-x-auto border rounded-lg max-h-[45vh]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs py-2 px-2">#</TableHead>
                    {preview.system_fields.filter(f => mappings[f.field]).map(f => (
                      <TableHead key={f.field} className="text-xs py-2 px-2 whitespace-nowrap">{f.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappedPreview.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs py-1 px-2 text-slate-400">{i + 1}</TableCell>
                      {preview.system_fields.filter(f => mappings[f.field]).map(f => (
                        <TableCell key={f.field} className="text-xs py-1 px-2 whitespace-nowrap max-w-[200px] truncate">
                          {row[f.field] || <span className="text-slate-300">—</span>}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
              This will import <strong>{preview.total_rows}</strong> {typeLabel.toLowerCase()}. Each row creates one record.
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>Back to Mapping</Button>
              <Button onClick={handleImport} disabled={loading || !requiredFieldsMapped} data-testid="import-execute-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Import {preview.total_rows} {typeLabel}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Results */}
        {step === 4 && importResult && (
          <div className="space-y-4 text-center py-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold" data-testid="import-success-msg">{importResult.message}</h3>
            <div className="flex justify-center gap-6 text-sm">
              <div><span className="text-slate-500">Total Rows:</span> <strong>{importResult.total_rows}</strong></div>
              <div><span className="text-slate-500">Imported:</span> <strong className="text-emerald-600">{importResult.created}</strong></div>
              {importResult.errors?.length > 0 && (
                <div><span className="text-slate-500">Errors:</span> <strong className="text-red-600">{importResult.errors.length}</strong></div>
              )}
            </div>
            {importResult.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-left text-xs text-red-700 max-h-32 overflow-y-auto">
                {importResult.errors.map((err, i) => <p key={i}>{err}</p>)}
              </div>
            )}
            <Button onClick={handleClose} data-testid="import-done-btn">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
